import { useState, useEffect } from "react";

/**
 * Returns a humanised countdown string that auto-refreshes every minute.
 * Example outputs:
 *   "6 días, 3 h"  |  "14 h 22 min"  |  "38 min"  |  "expirado"
 *
 * `critical` turns true when <7 days remain, `urgent` when <24h.
 */
export function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [targetIso]);

  if (!targetIso) {
    return { text: "", ms: null, critical: false, urgent: false, expired: false };
  }

  let targetMs;
  try { targetMs = new Date(targetIso).getTime(); }
  catch { return { text: "", ms: null, critical: false, urgent: false, expired: false }; }
  const diff = targetMs - now;

  if (diff <= 0) {
    return { text: "expirado", ms: 0, critical: true, urgent: true, expired: true };
  }

  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  let text;
  if (days >= 2) text = `${days} días, ${hours % 24} h`;
  else if (days === 1) text = `1 día, ${hours % 24} h`;
  else if (hours >= 1) text = `${hours} h ${mins % 60} min`;
  else text = `${mins} min`;

  return {
    text,
    ms: diff,
    critical: days < 7,
    urgent: days < 1,
    expired: false,
  };
}
